import { View } from '@yleisradio/areena-types';
import { Container } from 'components/Container';
import { GridContainer, GridElement } from 'components/Grid';
import ViewMenus from 'components/View/ViewMenus/ViewMenus';
import { useAnalyticsForView } from 'hooks/analytics';
import { isListArray } from 'utils/content';
import { Channel } from './Channel/Channel';
import { SelectableChannel } from './Channel/SelectableChannel';
import styles from './Guide.module.scss';
import { GuideHeader } from './GuideHeader';
import { DatePicker } from './GuideHeader/DatePicker';
import { PastProgramsToggle } from './GuideHeader/PastProgramsToggle';
import { useShowPastPrograms } from './useShowPastPrograms';
import { useCallback, useEffect, useState } from 'react';
import { randomUUID } from 'utils/uuid';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { SummaryViewEventParams } from 'services/snowplowAnalytics/contextGenerators';
import { guideViewEventGenerator } from 'services/snowplowAnalytics/viewEventGenerators';
import { findLabel } from 'utils/card';

interface GuideProps {
  /** Currently selected EPG date (YYYY-MM-DD) */
  currentDate: string;
  /** First available EPG date (YYYY-MM-DD) */
  firstDate: string;
  /** Last available EPG date (YYYY-MM-DD) */
  lastDate: string;
  isFutureDate: boolean;
  isToday: boolean;
  view: View;
}

export const Guide = (props: GuideProps) => {
  const [viewUuid, setViewUuid] = useState<string | undefined>();

  const { tabs: channels = [], title, menus } = props.view;
  const [isShowPastProgramsToggledOn, setShowPastProgramsToggle] =
    useShowPastPrograms();

  useAnalyticsForView(props.view.analytics);

  const { currentDate } = props;

  const { areenaService } = useAreenaService();

  useEffect(() => {
    const uuid = randomUUID();
    setViewUuid(uuid);
  }, [areenaService]);

  useEffect(() => {
    if (!window.yleSnowplow) {
      throw new Error('Snowplow tracker is not available');
    }
    if (viewUuid) {
      const { event, context } = guideViewEventGenerator({
        viewUuid,
        date: currentDate,
        service: areenaService,
        title,
        viewMode: 'full',
      });

      window.yleSnowplow('trackSelfDescribingEvent', { event, context });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUuid]);

  const sendSummaryViewEvent = useCallback(
    ({ viewUuid, card }: SummaryViewEventParams) => {
      if (!window.yleSnowplow) {
        throw new Error('Snowplow tracker is not available');
      }
      const itemId = findLabel(card.labels, 'itemId')?.raw;

      const isProgram = card.pointer?.type === 'program';

      const programId = isProgram ? itemId : undefined;
      const entityId = !isProgram ? itemId : undefined;

      const { event, context } = guideViewEventGenerator({
        viewUuid,
        date: currentDate,
        service: areenaService,
        title,
        programId,
        entityId,
        viewMode: 'summary',
      });

      window.yleSnowplow('trackSelfDescribingEvent', { event, context });
    },
    [areenaService, currentDate, title]
  );

  const channelElements = channels.map((channel) => {
    const channelContent = channel.content;

    return (
      channelContent &&
      channelContent.length > 0 &&
      isListArray(channelContent) && (
        <GridElement
          key={channel.title}
          mobile={12}
          mobileLandscape={6}
          desktopSmall={6}
        >
          {channelContent.length > 1 && (
            <SelectableChannel
              isToday={props.isToday}
              isFutureDate={props.isFutureDate}
              showExpired={isShowPastProgramsToggledOn}
              channel={channel}
              sendSummaryViewEvent={sendSummaryViewEvent}
            />
          )}
          {channelContent.length === 1 && (
            <Channel
              isToday={props.isToday}
              isFutureDate={props.isFutureDate}
              showExpired={isShowPastProgramsToggledOn}
              title={channel.title}
              image={channel.image}
              channelContent={channelContent[0]!}
              sendSummaryViewEvent={sendSummaryViewEvent}
            />
          )}
        </GridElement>
      )
    );
  });

  return (
    <main id="maincontent" className={styles.root}>
      {menus ? <ViewMenus menus={menus} /> : null}
      <Container>
        <GuideHeader
          title={title}
          datePicker={
            <DatePicker
              currentDate={props.currentDate}
              firstDate={props.firstDate}
              lastDate={props.lastDate}
            />
          }
          pastProgramsToggle={
            props.isToday ? (
              <PastProgramsToggle
                checked={isShowPastProgramsToggledOn}
                toggle={() => {
                  setShowPastProgramsToggle(!isShowPastProgramsToggledOn);
                }}
              />
            ) : null
          }
        />
        <GridContainer gap="small">{channelElements}</GridContainer>
      </Container>
    </main>
  );
};
